import React from 'react';

import { tv } from '../../tv';
import { FieldMessage, fieldStyles, useFieldMessageIds } from '../field/field-shell';
import { resolveFieldState } from '../field/field-state';
import { RequiredMark } from '../field/required-mark';

const fieldsetStyles = tv({
  slots: {
    /* A fieldset's native border, padding and min-width are browser chrome. */
    root: 'ck:m-0 ck:flex ck:min-w-0 ck:flex-col ck:gap-2 ck:border-0 ck:p-0',
    /* A rendered legend is not a flex item, so the fieldset's gap skips it: it
     * takes the field's 8px label step itself. */
    legend: 'ck:mb-2 ck:p-0',
    /* The fields sit 16px under the legend and its description, as fields sit
     * apart; the group's error keeps the 8px message step under them. */
    body: 'ck:mt-2 ck:flex ck:min-w-0 ck:flex-col ck:gap-4',
  },
});

export interface FieldsetProps {
  /** The group's visible name, read before every field in it. */
  legend: string;
  /**
   * What the group is for, under the legend and before its fields: an
   * instruction for the whole group, so it stays when one field shows an error.
   * Inline content only.
   */
  description?: React.ReactNode;
  /** A message for the group as a whole ("Add at least one database"), under its fields. Not shown read-only. */
  error?: string;
  /**
   * Marks the legend with an asterisk, read aloud: a group cannot carry
   * `aria-required`, so its mark is the signal. Not shown read-only.
   */
  isRequired?: boolean;
  /** The group's values cannot be changed: no required mark, no error. Its fields take `isReadOnly` too. */
  isReadOnly?: boolean;
  /**
   * The group is unavailable: the native fieldset is disabled, so every
   * control in it is out of the tab order and reported unavailable. Its
   * fields still take `isDisabled` for their own look.
   */
  isDisabled?: boolean;
  /** The fields, 16px apart. */
  children: React.ReactNode;
}

/**
 * ComplyKit Fieldset: a named group of fields, the native fieldset and
 * legend, for settings that belong together (a nested object of a schema
 * form, a list of rows). The legend takes the field label's type, so a group
 * reads as one more field in the column; its fields keep their own labels.
 *
 * Not for one control's choices: a CheckboxGroup or RadioGroup names those.
 */
export const Fieldset = React.forwardRef<HTMLFieldSetElement, FieldsetProps>(function Fieldset(
  {
    legend,
    description,
    error: errorProp,
    isRequired: requiredProp = false,
    isReadOnly: readOnlyProp = false,
    isDisabled: disabledProp = false,
    children,
  },
  ref,
) {
  const { error, isRequired, isDisabled } = resolveFieldState({
    error: errorProp,
    isRequired: requiredProp,
    isReadOnly: readOnlyProp,
    isDisabled: disabledProp,
  });
  const { root, legend: legendClass, body } = fieldsetStyles();
  const { label, description: descriptionClass } = fieldStyles({
    labelPosition: 'top',
    size: 'md',
  });
  const { descriptionId, errorId } = useFieldMessageIds(description, error);
  const describedBy = [description ? descriptionId : undefined, error ? errorId : undefined]
    .filter(Boolean)
    .join(' ');

  return (
    <fieldset
      ref={ref}
      className={root()}
      aria-describedby={describedBy || undefined}
      disabled={isDisabled || undefined}
      data-component="fieldset"
    >
      <legend className={legendClass({ className: label() })}>
        {legend}
        {isRequired && <RequiredMark announces />}
      </legend>
      {description && (
        <div id={descriptionId} className={descriptionClass()}>
          {description}
        </div>
      )}
      <div className={body()}>{children}</div>
      <FieldMessage error={error} descriptionId={descriptionId} errorId={errorId} />
    </fieldset>
  );
});
